import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { FileUpload } from '../../../../../../components/ui/FileUpload';
import { Upload, FileText, X, AlertCircle, CheckCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { createNDTRepairLog } from '../../../../../../redux/ProductionData/actions';
import axiosConfig from '../../../../../../redux/ProductionData/axiosConfig';
import axiosConfigWeld from '../../../../../../redux/WeldData/axiosConfig';
import Notification from '../../../../../../components/ui/custom-notification';
import dayjs from 'dayjs';

interface UploadNDTRepairLogDataDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete?: () => void;
    weld: any;
}

const NDT_BULK_PATH = 'ntdIndicationLog/bulkCreateUpdateNtdIndicationLogs';
const DEFAULT_PIPE_ID = 0;

const readOptionalString = (v: unknown): string => (v === undefined || v === null ? '' : String(v).trim());

const getPipeIdForProjectName = (projectNameRaw: unknown, fallbackPipeId: number): number => {
    const name = String(projectNameRaw ?? '').trim().toLowerCase();
    if (name === 'ruya') return 140;
    if (name === 'salamanca') return 141;
    return fallbackPipeId;
};

// Defect Identification mapping (abbreviation to full name)
const DEFECT_IDENTIFICATION_MAP: Record<string, string> = {
    'BT': 'Burn Through',
    'IC': 'Internal Concavity',
    'IPD': 'Inadequate Penetration Due to High-Low',
    'C': 'Crack',
    'ICP': 'Inadequate Cross Penetration',
    'CP': 'Cluster Porosity',
    'IF': 'Incomplete Fusion',
    'ISI': 'Isolated Slag Inclusion',
    'ESI': 'Elongated Slag Inclusions',
    'IFD': 'Incompleted Fusion Due to Cold Lap',
    'IU': 'Undercutting (Root)',
    'EU': 'Undercutting (Cap)',
    'IP': 'Inadequate Penetration',
    'P': 'Porosity',
    'HB': 'Hollow Bead'
};

// Defect Classification mapping (abbreviation to full name)
const DEFECT_CLASSIFICATION_MAP: Record<string, string> = {
    'AR': 'Accumulation of Relevant Indications',
    'LB': 'Linear Burned',
    'LS': 'Linear Surface',
    'T': 'Transverse',
    'VC': 'Volumetric Cluster',
    'VI': 'Volumetric Individual',
    'VR': 'Volumetric Root'
};

// Helper function to convert abbreviation to full name
const convertAbbreviationToFullName = (value: string, map: Record<string, string>): string => {
    if (!value) return '';
    const trimmedValue = String(value).trim().toUpperCase();
    // Check if it's already a full name (contains spaces or is longer than 3 chars)
    if (trimmedValue.length > 3 || trimmedValue.includes(' ')) {
        // Check if it matches any full name in the map
        const fullNameMatch = Object.values(map).find(fullName =>
            fullName.toUpperCase() === trimmedValue
        );
        if (fullNameMatch) {
            return fullNameMatch;
        }
        // Return as is if it's already a full name
        return String(value).trim();
    }
    // Convert abbreviation to full name
    return map[trimmedValue] || String(value).trim();
};

// Excel column headers mapping
const EXCEL_COLUMNS = [

    'Weld ID',
    'WPS Number',
    'Pipe Dia',
    'Wall Thickness',
    'Date',
    'Time',
    'Shift',
    'Defect Classification',
    'Defect Identification',
    "o'clock Location",
    'Defect Location Start',
    'Defect Location End',
    'Defect Length',
    'Defect Depth',
    'Defect Height',
    'Welders N.',
    'Welder Rep. No',
    'Location',
    'Defect type',
    'Pass Name',
    'Time',
    'Evaluation',
    'Critical Weld',
    'Repair',
    'Comments',
    'Corrective Actions',
    'Accepted workmanship',
    'RCA',
    'Root cause',

];

export const UploadNDTRepairLogDataDialog: React.FC<UploadNDTRepairLogDataDialogProps> = ({
    isOpen,
    onClose,
    onUploadComplete,
    weld
}) => {
    const dispatch = useDispatch();
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [excelData, setExcelData] = useState<any[]>([]);
    const [isProcessingExcel, setIsProcessingExcel] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
    const projectId = Number(weld.projectId || weld.selectedPipe?.projectId || 0);
    const pipeId = Number(weld.selectedPipe?.pipeId || DEFAULT_PIPE_ID);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (!isOpen) {
            setExcelFile(null);
            setExcelData([]);
            setIsUploading(false);
            setIsProcessingExcel(false);
            setValidationErrors([]);
        }
    }, [isOpen]);

    // Convert Excel serial date to JavaScript Date
    const convertDate = (excelDate: any): string => {
        // Return empty string if date is not available
        if (!excelDate || excelDate === '' || excelDate === null || excelDate === undefined) {
            return '';
        }

        try {
            // If it's already a string, try to parse it
            if (typeof excelDate === 'string') {
                const trimmedDate = excelDate.trim();
                // Return empty if string is empty after trimming
                if (trimmedDate === '') {
                    return '';
                }

                // Already ISO
                if (trimmedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return trimmedDate;
                }

                // Excel serial date sometimes arrives as a numeric string (e.g. "45894.00011574074")
                if (/^\d+(\.\d+)?$/.test(trimmedDate)) {
                    const asNum = parseFloat(trimmedDate);
                    if (!Number.isNaN(asNum) && asNum > 0) {
                        const excelEpoch = new Date(1899, 11, 30); // Excel epoch used elsewhere in file
                        const date = new Date(excelEpoch.getTime() + asNum * 86400000);
                        return Number.isNaN(date.getTime()) ? '' : dayjs(date).format('YYYY-MM-DD');
                    }
                }

                // Common Excel-exported date strings (e.g. 8/25/25, 08/25/2025)
                const parsedFlexible = dayjs(trimmedDate, ['M/D/YY', 'MM/DD/YY', 'M/D/YYYY', 'MM/DD/YYYY'], true);
                if (parsedFlexible.isValid()) {
                    return parsedFlexible.format('YYYY-MM-DD');
                }

                // Fallback to native Date parsing if possible
                const parsedDate = new Date(trimmedDate);
                if (!isNaN(parsedDate.getTime())) {
                    return dayjs(parsedDate).format('YYYY-MM-DD');
                }

                // If can't parse, return empty string instead of current date
                return '';
            }

            // If it's a number (Excel serial date)
            if (typeof excelDate === 'number') {
                // Check if it's a valid number
                if (isNaN(excelDate) || excelDate <= 0) {
                    return '';
                }
                // Excel serial date: days since 1899-12-30 (accounts for Excel's 1900 leap-year bug)
                const excelEpoch = new Date(1899, 11, 30);
                const date = new Date(excelEpoch.getTime() + excelDate * 86400000);
                // Validate the converted date
                if (isNaN(date.getTime())) {
                    return '';
                }
                return dayjs(date).format('YYYY-MM-DD');
            }

            // For any other type, return empty string
            return '';
        } catch (error) {
            console.error('Error converting date:', error);
            // Return empty string instead of current date on error
            return '';
        }
    };

    // Download template Excel file
    const handleDownloadTemplate = () => {
        try {
            // Create workbook
            const wb = XLSX.utils.book_new();

            // Create worksheet with headers
            const wsData = [EXCEL_COLUMNS];
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // Set column widths
            const colWidths = EXCEL_COLUMNS.map(() => ({ wch: 20 }));
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'NDT Repair Log Data');

            // Generate Excel file
            XLSX.writeFile(wb, 'NDT_Repair_Log_Template.xlsx');
            Notification.success('Template downloaded successfully');
        } catch (error) {
            console.error('Error downloading template:', error);
            Notification.error('Failed to download template');
        }
    };

    // Process Excel file
    const processExcelFile = async (file: File) => {
        setIsProcessingExcel(true);
        setValidationErrors([]);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });

            // Get first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            if (!worksheet) {
                Notification.error('No data found in Excel file');
                setIsProcessingExcel(false);
                return;
            }

            // Convert to JSON
            const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (data.length < 2) {
                Notification.error('Excel file must have at least a header row and one data row');
                setIsProcessingExcel(false);
                return;
            }

            // Get headers (first row)
            const headers = (data[0] as any[]).map((h: any) => String(h || '').trim());

            // Process data rows
            const processedData: any[] = [];
            const errors: string[] = [];

            for (let i = 1; i < data.length; i++) {
                const row = data[i] as any[];
                if (!row || row.length === 0) continue;

                const rowData: any = {};
                headers.forEach((header, index) => {
                    rowData[header] = row[index] !== undefined ? row[index] : '';
                });

                // Convert to RepairLogData format
                // Read Weld ID from Excel, or use default from weld prop
                const weldId = rowData['Weld ID'] || rowData['Weld ID'] || rowData['weldId'] || rowData['WeldId'];

                const excelProjectId = parseInt(String(rowData['Project ID'] || rowData['projectId'] || weld.projectId || '0'), 10) || 0;
                const excelPipeId = parseInt(String(rowData['Pipe ID'] || rowData['pipeId'] || weld.selectedPipe?.pipeId || '0'), 10) || 0;
                const repairLogData: any = {
                    project_id: projectId,
                    pipe_id: pipeId,
                    weld_id: weldId,
                    date: convertDate(rowData['Date'] || rowData['date'] || ''),
                    shift: String(rowData['Shift'] || rowData['shift'] || '').trim(),
                    weldersNo: String(rowData['Welders N.'] || rowData['Welders N'] || rowData['weldersNo'] || '').trim(),
                    welderRepairNo: String(rowData['Welder Rep. No'] || rowData['Welder Rep No'] || rowData['welderRepNo'] || '').trim(),
                    location: String(rowData['Location'] || rowData['location'] || '').trim(),
                    defectType: String(rowData['Defect type'] || rowData['Defect Type'] || rowData['defectType'] || '').trim(),
                    defectLocationStart: parseFloat(String(rowData['Defect Location Start'] || rowData['defectLocationStart'] || '0')) || 0,
                    defectLocationEnd: parseFloat(String(rowData['Defect Location End'] || rowData['defectLocationEnd'] || '0')) || 0,
                    defectLength: parseFloat(String(rowData['Defect Length'] || rowData['defectLength'] || '0')) || 0,
                    defectDepth: parseFloat(String(rowData['Defect Depth'] || rowData['defectDepth'] || '0')) || 0,
                    defectHeight: parseFloat(String(rowData['Defect Height'] || rowData['defectHeight'] || '0')) || 0,
                    comments: String(rowData['Comments'] || rowData['comments'] || '').trim(),
                    rootCause: String(rowData['Root cause'] || rowData['Root Cause'] || rowData['rootCause'] || '').trim(),
                    // Calculate o'clock location based on defect location start/end and pipe diameter
                    oclockLocation: (() => {
                        // If o'clock location is provided in Excel, use it; otherwise calculate
                        const excelOclock = String(rowData["o'clock Location"] || rowData["o'clock"] || rowData['oclockLocation'] || '').trim();
                        if (excelOclock) {
                            return excelOclock;
                        }

                        // Calculate o'clock location
                        const start = parseFloat(String(rowData['Defect Location Start'] || rowData['defectLocationStart'] || '0')) || 0;
                        const end = parseFloat(String(rowData['Defect Location End'] || rowData['defectLocationEnd'] || '0')) || 0;
                        const pipeDia = rowData['Pipe Dia'] || rowData['Pipe Dia (in)'] || rowData['pipeDia']
                            ? (parseFloat(String(rowData['Pipe Dia'] || rowData['Pipe Dia (in)'] || rowData['pipeDia'] || '0')) || 0)
                            : ((weld as any).pipe_diameter || (weld as any).pipeDiameter || 20);

                        if (start !== null && end !== null && !isNaN(start) && !isNaN(end) && start > 0 && end > 0 && pipeDia) {
                            // Calculate o'clock position based on midpoint of defect location (start and end)
                            // Assuming the defect location is measured along the pipe circumference
                            // Convert to clock position (0-12 o'clock)
                            // Use the midpoint (average) of start and end for the calculation
                            const midpoint = (start + end) / 2;
                            const pipeDiaInches = pipeDia || 20;
                            const circumference = Math.PI * pipeDiaInches * 25.4; // Convert inches to mm and calculate circumference
                            const position = (midpoint / circumference) * 12;
                            let oclock = Math.round(position) % 12;
                            if (oclock === 0) oclock = 12;
                            const finalOclock = 12 - oclock;
                            return `${finalOclock} o'clock`;
                        }
                        return '';
                    })(),
                    repair: String(rowData['Repair'] || rowData['repair'] || '').trim(),
                    rca: String(rowData['RCA'] || rowData['rca'] || '').trim(),
                    passName: String(rowData['Pass Name'] || rowData['passName'] || rowData['Defect Name'] || rowData['defectName'] || '').trim(),
                    time: String(rowData['Time'] || rowData['time'] || '').trim(),
                    correctiveActions: String(rowData['Corrective Actions'] || rowData['CorrectiveActions'] || rowData['correctiveActions'] || '').trim(),
                    // Handle Defect Classification and Defect Identification - convert abbreviations to full strings
                    defectClassification: convertAbbreviationToFullName(
                        String(rowData['Defect Classification'] || rowData['defectClassification'] || '').trim(),
                        DEFECT_CLASSIFICATION_MAP
                    ),
                    defectIdentification: convertAbbreviationToFullName(
                        String(rowData['Defect Identification'] || rowData['defectIdentification'] || '').trim(),
                        DEFECT_IDENTIFICATION_MAP
                    ),
                    // Pipe Dia, Wall Thickness, WPS Number - use Excel value if provided, otherwise fallback to weld data
                    pipeDia: rowData['Pipe Dia'] || rowData['Pipe Dia (in)'] || rowData['pipeDia']
                        ? (parseFloat(String(rowData['Pipe Dia'] || rowData['Pipe Dia (in)'] || rowData['pipeDia'] || '0')) || 0)
                        : ((weld as any).pipe_diameter || (weld as any).pipeDiameter || 20),
                    wallThickness: rowData['Wall Thickness'] || rowData['Wall Thickness (in)'] || rowData['wallThickness']
                        ? (parseFloat(String(rowData['Wall Thickness'] || rowData['Wall Thickness (in)'] || rowData['wallThickness'] || '0')) || 0)
                        : ((weld as any).wall_thickness || (weld as any).thickness || 5),
                    wpsNumber: String(rowData['WPS Number'] || rowData['wpsNumber'] || weld.wpsNumber || '').trim(),
                    documents: [],
                    documentTags: [],
                    ndtIndicationDocId: null
                };

                // Handle evaluation
                const evaluation = String(rowData['Evaluation'] || rowData['evaluation'] || '').trim();
                if (evaluation.toLowerCase() === 'accept' || evaluation.toLowerCase() === 'yes') {
                    repairLogData.evaluation = 'Accept';
                } else if (evaluation.toLowerCase() === 'reject' || evaluation.toLowerCase() === 'no') {
                    repairLogData.evaluation = 'Reject';
                } else {
                    repairLogData.evaluation = 'Reject'; // Default
                }

                // Handle criticalWeld
                const criticalWeld = String(rowData['Critical Weld'] || rowData['CriticalWeld'] || rowData['criticalWeld'] || '').trim();
                if (criticalWeld.toLowerCase() === 'yes' || criticalWeld.toLowerCase() === 'true') {
                    repairLogData.criticalWeld = 'Yes';
                } else {
                    repairLogData.criticalWeld = 'No';
                }

                // Handle acceptedWorkmanship
                const acceptedWorkmanship = String(rowData['Accepted workmanship'] || rowData['Accepted Workmanship'] || rowData['acceptedWorkmanship'] || '').trim();
                if (acceptedWorkmanship.toLowerCase() === 'yes' || acceptedWorkmanship.toLowerCase() === 'true') {
                    repairLogData.acceptedWorkmanship = true;
                } else {
                    repairLogData.acceptedWorkmanship = false;
                }

                // Validate required fields
                // if (!repairLogData.date) {
                //     errors.push(`Row ${i + 1}: Date is required`);
                // }
                if (repairLogData.defectLocationStart === 0 && repairLogData.defectLocationEnd === 0) {
                    //  errors.push(`Row ${i + 1}: At least one of Defect Location Start or End is required`);
                    continue;
                }

                processedData.push(repairLogData);
            }

            if (errors.length > 0) {
                setValidationErrors(errors);
                Notification.error(`Found ${errors.length} validation error(s). Please check and fix them.`);
            } else {
                Notification.success(`Successfully processed ${processedData.length} row(s)`);
            }

            console.log('processedData', processedData);
            setExcelData(processedData);
        } catch (error) {
            console.error('Error processing Excel file:', error);
            Notification.error('Failed to process Excel file');
        } finally {
            setIsProcessingExcel(false);
        }
    };

    // Process Excel file
    const processExcelFile2 = async (file: File) => {
        setIsProcessingExcel(true);
        setValidationErrors([]);

        try {
            const lowerName = (file.name || '').toLowerCase();
            let workbook: XLSX.WorkBook;
            if (lowerName.endsWith('.csv')) {
                let csvText = await file.text();
                if (csvText.charCodeAt(0) === 0xfeff) {
                    csvText = csvText.slice(1);
                }
                workbook = XLSX.read(csvText, { type: 'string' });
            } else {
                const arrayBuffer = await file.arrayBuffer();
                workbook = XLSX.read(arrayBuffer, { type: 'array' });
            }

            // Get first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];



            if (!worksheet) {
                Notification.error('No data found in Excel file');
                setIsProcessingExcel(false);
                return;
            }

            // Convert to JSON - defval so empty cells are read; ensures we get all rows
            const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

            if (data.length < 2) {
                Notification.error('Excel file must have at least a header row and one data row');
                setIsProcessingExcel(false);
                return;
            }
            console.log('data length', data.length);
            // Get headers (first row)
            const headers = (data[0] as any[]).map((h: any) => String(h || '').trim());

            // Process data rows
            const processedData: any[] = [];
            const errors: string[] = [];
            const rawRowsSample: Array<{ rowIndex: number; rowData: Record<string, any> }> = [];

            const normalizeKey = (k: unknown) => String(k ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const buildNormalizedRow = (rowData: any) => {
                const normalized: Record<string, any> = {};
                Object.keys(rowData || {}).forEach((k) => {
                    const nk = normalizeKey(k);
                    if (nk) normalized[nk] = rowData[k];
                });
                return normalized;
            };
            const readStr = (rowN: Record<string, any>, ...keys: string[]) => {
                for (const k of keys) {
                    const v = rowN[normalizeKey(k)];
                    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
                }
                return '';
            };
            const readNum = (rowN: Record<string, any>, ...keys: string[]) => {
                const s = readStr(rowN, ...keys).replace(/,/g, '').trim();
                const n = parseFloat(s);
                return Number.isNaN(n) ? 0 : n;
            };

            for (let i = 1; i < data.length; i++) {
                const row = data[i] as any[];
                if (!row) continue;

                const rowData: any = {};
                headers.forEach((header, index) => {
                    rowData[header] = row[index] !== undefined && row[index] !== null ? row[index] : '';
                });
                const rowN = buildNormalizedRow(rowData);
                if (rawRowsSample.length < 3) {
                    rawRowsSample.push({
                        rowIndex: i + 1,
                        rowData
                    });
                }

                // Skip only completely empty rows
                const hasAnyData = headers.some((_, idx) => {
                    const v = row[idx];
                    return v !== undefined && v !== null && String(v).trim() !== '';
                });
                if (!hasAnyData) continue;

                // Convert to RepairLogData format (supports the Excel format you shared)
                const weldJointNumber = readStr(rowN, 'Weld Joint Number', 'Weld Joint No', 'Weld ID', 'WeldId', 'weld_id');
                const projectName = readStr(rowN, 'Project Name', 'projectName');
                const derivedPipeId = getPipeIdForProjectName(projectName, pipeId || DEFAULT_PIPE_ID);
                const subProjectName = readStr(rowN, 'Sub Project', 'Sub-project', 'SubProject', 'subProject', 'sub_project', 'subProjectName');
                const dateCell = rowData['Date'] ?? rowData['date'] ?? rowN[normalizeKey('Date')] ?? '';
                const idRaw = readStr(rowN, 'id', 'ID', 'NDT Log ID', 'NDT Log Id');
                const parsedId = idRaw ? Number(idRaw) : NaN;

                const repairLogData: any = {
                    ...(Number.isFinite(parsedId) ? { id: parsedId } : {}),
                    project_id: projectId,
                    pipe_id: derivedPipeId,
                    subProjectName,
                    weld_id: weldJointNumber ? weldJointNumber : `W${i}`,
                    date: convertDate(dateCell),
                    shift: readStr(rowN, 'Shift'),
                    weldersNo: readStr(rowN, 'Welders No.', 'Welders No', 'Welders N.', 'Welders N', 'welderNumber', 'welder_number', 'weldersNo'),
                    welderRepairNo: readStr(rowN, 'Welder Repair No.', 'Welder Repair No', 'Welder Rep. No', 'Welder Rep No', 'welderRepairNo', 'welder_repair_no'),
                    location: readStr(rowN, 'Location'),
                    defectType: readStr(rowN, 'Defect type', 'Defect Type', 'defectType'),
                    defectLocationStart: readNum(rowN, 'Defect Location Start (mm)', 'Defect Location Start', 'defectLocationStart'),
                    defectLocationEnd: readNum(rowN, 'Defect Location End (mm)', 'Defect Location End', 'defectLocationEnd'),
                    defectLength: readNum(rowN, 'Defect Length (mm)', 'Defect Length', 'defectLength'),
                    defectDepth: readNum(rowN, 'Defect Depth (mm)', 'Defect Depth', 'defectDepth'),
                    defectHeight: readNum(rowN, 'Defect Height (mm)', 'Defect Height', 'defectHeight'),
                    comments: readStr(rowN, 'Comments'),
                    rootCause: readStr(rowN, 'Root cause', 'Root Cause', 'rootCause'),
                    // Calculate o'clock location based on defect location start/end and pipe diameter
                    oclockLocation: readStr(rowN, "o'clock Location", "o'clock", 'oclockLocation'),
                    repair: readStr(rowN, 'Repair'),
                    rca: readStr(rowN, 'RCA'),
                    passName: readStr(rowN, 'Pass Name', 'passName') || readStr(rowN, 'Defect Name', 'defectName'),
                    time: readStr(rowN, 'Time'),
                    correctiveActions: readStr(rowN, 'Corrective Actions', 'CorrectiveActions', 'correctiveActions'),
                    // Handle Defect Classification and Defect Identification - convert abbreviations to full strings
                    defectClassification: convertAbbreviationToFullName(
                        readStr(rowN, 'Defect Classification', 'defectClassification'),
                        DEFECT_CLASSIFICATION_MAP
                    ),
                    defectIdentification: convertAbbreviationToFullName(
                        readStr(rowN, 'Defect Identification', 'defectIdentification'),
                        DEFECT_IDENTIFICATION_MAP
                    ),
                    // Pipe Dia, Wall Thickness, WPS Number - use Excel value if provided, otherwise fallback to weld data
                    pipeDia: readNum(rowN, 'Pipe Dia (inch)', 'Pipe Dia (in)', 'Pipe Dia', 'pipeDia') || 20,
                    wallThickness: readNum(rowN, 'Wall Thickness (mm)', 'Wall Thickness (in)', 'Wall Thickness', 'wallThickness') || 5,
                    wpsNumber: readStr(rowN, 'WPS Number', 'wpsNumber'),
                    documents: [],
                    documentTags: [],
                    ndtIndicationDocId: []
                };

                // Handle evaluation
                const evaluation = readStr(rowN, 'Evaluation', 'evaluation');
                if (evaluation.toLowerCase() === 'accept' || evaluation.toLowerCase() === 'accepted' || evaluation.toLowerCase() === 'yes') {
                    repairLogData.evaluation = 'Accepted';
                } else if (evaluation.toLowerCase() === 'reject' || evaluation.toLowerCase() === 'rejected' || evaluation.toLowerCase() === 'no') {
                    repairLogData.evaluation = 'Rejected';
                } else {
                    repairLogData.evaluation = evaluation
                }

                // Handle criticalWeld
                const criticalWeld = readStr(rowN, 'Critical weld', 'Critical Weld', 'CriticalWeld', 'criticalWeld');
                if (criticalWeld.toLowerCase() === 'yes' || criticalWeld.toLowerCase() === 'true') {
                    repairLogData.criticalWeld = 'Yes';
                } else {
                    repairLogData.criticalWeld = criticalWeld
                }

                // Handle acceptedWorkmanship
                const acceptedWorkmanship = readStr(rowN, 'Accepted workmanship', 'Accepted Workmanship', 'acceptedWorkmanship');
                if (acceptedWorkmanship.toLowerCase() === 'yes' || acceptedWorkmanship.toLowerCase() === 'true') {
                    repairLogData.acceptedWorkmanship = true;
                } else {
                    repairLogData.acceptedWorkmanship = false;
                }

                // Process all rows with data - do not skip based on defect location so full Excel row count is processed
                processedData.push(repairLogData);
            }

            if (errors.length > 0) {
                setValidationErrors(errors);
                Notification.error(`Found ${errors.length} validation error(s). Please check and fix them.`);
            } else {
                Notification.success(`Successfully processed ${processedData.length} row(s)`);
            }
            console.log(`Pipe 140 (Ruya) ${processedData.filter((item) => item.pipe_id === 140).length} row(s)`);
            console.log(`Pipe 141 (Salamanca) ${processedData.filter((item) => item.pipe_id === 141).length} row(s)`);
            console.log(`Pipe ${pipeId} (fallback) ${processedData.filter((item) => item.pipe_id === pipeId).length} row(s)`);
            console.log('processedData length', processedData);
            console.log('raw Excel rows sample (first 3)', rawRowsSample);
            console.log('processedData sample (first 3)', processedData.slice(0, 3).map((r) => ({
                weld_id: r.weld_id,
                pipe_id: r.pipe_id,
                subProjectName: r.subProjectName,
                date: r.date,
                weldersNo: r.weldersNo,
                welderRepairNo: r.welderRepairNo,
                defectType: r.defectType,
                evaluation: r.evaluation,
                criticalWeld: r.criticalWeld,
                acceptedWorkmanship: r.acceptedWorkmanship,
                rca: r.rca,
                rootCause: r.rootCause,
                correctiveActions: r.correctiveActions
            })));
            setExcelData(processedData);
        } catch (error) {
            console.error('Error processing Excel file:', error);
            Notification.error('Failed to process Excel file');
        } finally {
            setIsProcessingExcel(false);
        }
    };

    // API key → Excel header name (for TLOG upload from Excel with date headers)
    const HEADER_TLOG_DATA: Record<string, string> = {
        welduid_s_event_torch: "File Name",
        weld_number: "Weld ID",
        zone_name: "Pass Name",
        time: "Start Time",
        job_number: "Job Number",
        travel_speed: "Travel Spe",
        current: "Record Current",
        volts: "Record Voltage",
        heat: "Heat Input(Kj/in)",
        s_time: "Start Time",
        e_time: "Finish time",
        duration: "Duration",
     
        current_min: "Current Min",
        current_avg: "Current Avg",
        current_max: "Current Max",
        voltage_min: "Voltage Min",
        voltage_avg: "Voltage Avg",
        voltage_max: "Voltage Max",
        weld_time: "Duration (s)",
    };
    // Process TLOG spreadsheet (Excel or CSV — same column layout)
    const processExcelFileTLOGData = async (file: File) => {
        setIsProcessingExcel(true);
        setValidationErrors([]);

        try {
            const lowerName = (file.name || '').toLowerCase();
            let workbook: XLSX.WorkBook;
            if (lowerName.endsWith('.csv')) {
                let csvText = await file.text();
                if (csvText.charCodeAt(0) === 0xfeff) {
                    csvText = csvText.slice(1);
                }
                workbook = XLSX.read(csvText, { type: 'string' });
            } else {
                const arrayBuffer = await file.arrayBuffer();
                workbook = XLSX.read(arrayBuffer, { type: 'array' });
            }

            // Get first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];



            if (!worksheet) {
                Notification.error('No data found in file');
                setIsProcessingExcel(false);
                return;
            }

            // Convert to JSON - defval so empty cells are read; ensures we get all rows
            const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

            if (data.length < 2) {
                Notification.error('File must have at least a header row and one data row');
                setIsProcessingExcel(false);
                return;
            }
            console.log('data length', data.length);
            // Get headers (first row)
            const headers = (data[0] as any[]).map((h: any) => String(h || '').trim());

            // Process data rows
            const processedData: any[] = [];
            const errors: string[] = [];

            // Helper to read numeric from multiple possible column names
            const readNum = (rowData: any, ...keys: string[]) => {
                const raw = keys.reduce((acc, k) => acc || rowData[k], '') as string;
                const s = String(raw || '0').replace(/,/g, '').trim();
                return parseFloat(s) || 0;
            };

            for (let i = 2; i < data.length; i++) {
                const row = data[i] as any[];
                if (!row) continue;

                const rowData: any = {};
                headers.forEach((header, index) => {
                    rowData[header] = row[index] !== undefined && row[index] !== null ? row[index] : '';
                });

                // Skip only completely empty rows
                const hasAnyData = headers.some((_, idx) => {
                    const v = row[idx];
                    return v !== undefined && v !== null && String(v).trim() !== '';
                });
                if (!hasAnyData) continue;

             
                // Build TLOG JSON from Excel row using HEADER_TLOG_DATA (includes date headers: Start Time, End Time, Duration)
                const getCell = (excelHeader: string, apiKey: string) => {
                    const v = rowData[excelHeader];
                    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
                    const camel = apiKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                    return rowData[camel] ?? '';
                };
                const tlogData: any = {
                    project_id: projectId,
                    pipe_id: pipeId,
                    ...Object.fromEntries(
                        Object.entries(HEADER_TLOG_DATA).map(([apiKey, excelHeader]) => [
                            apiKey,
                            getCell(excelHeader, apiKey),
                        ])
                    ),
                };
                // weld_number from Excel "Weld ID" column (try alternate header names if missing)
                if (tlogData.weld_number === undefined || tlogData.weld_number === null || String(tlogData.weld_number).trim() === '') {
                    const weldIdVal = rowData['Weld Number'] ?? rowData['Weld Id'] ?? rowData['weld id'] ?? rowData['weldNumber'] ?? '';
                    tlogData.weld_number = weldIdVal !== undefined && weldIdVal !== null ? String(weldIdVal).trim() : '';
                }
                // Store name only for welduid_s_event_torch (remove .txt extension)
                if (typeof tlogData.welduid_s_event_torch === 'string') {
                    tlogData.welduid_s_event_torch = tlogData.welduid_s_event_torch.replace(/\.txt$/i, '');
                }
                // Parse date from filename (e.g. W5_ROOT_20260128_135744 → 2026-01-28) for time-only conversion
                let baseDateFromFile: { year: number; month: number; day: number } | null = null;
                const fileName = String(tlogData.welduid_s_event_torch || '');
                const dateMatch = fileName.match(/(\d{8})/);
                if (dateMatch) {
                    const ymd = dateMatch[1];
                    const year = parseInt(ymd.slice(0, 4), 10);
                    const month = parseInt(ymd.slice(4, 6), 10) - 1; // JS month 0-indexed
                    const day = parseInt(ymd.slice(6, 8), 10);
                    if (year >= 1900 && year <= 2100 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
                        baseDateFromFile = { year, month, day };
                    }
                }
                // Convert time, s_time, e_time to Unix timestamp (number, seconds since epoch)
                const toUnixTimestamp = (val: unknown): number => {
                    if (val === undefined || val === null || val === '') return 0;
                    const n = Number(val);
                    // Already a Unix-like timestamp (seconds, 9+ digits)
                    if (!Number.isNaN(n) && n > 1e9 && n < 1e11) return Math.floor(n);
                    // Excel serial date (days since 1900-01-01)
                    if (!Number.isNaN(n) && n > 1000 && n < 100000) {
                        const excelEpoch = new Date(1899, 11, 30);
                        const d = new Date(excelEpoch.getTime() + n * 86400000);
                        return Math.floor(d.getTime() / 1000);
                    }
                    const s = String(val).trim();
                    if (!s) return 0;
                    let date = new Date(s);
                    if (!Number.isNaN(date.getTime())) return Math.floor(date.getTime() / 1000);
                    // Time only (e.g. "11:54:00") – use date from filename (e.g. 20260128) or fallback to today
                    const [h, m, sec] = s.split(/[:\s]/).map(Number);
                    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
                        if (baseDateFromFile) {
                            date = new Date(baseDateFromFile.year, baseDateFromFile.month, baseDateFromFile.day, h, Number.isNaN(m) ? 0 : m, Number.isNaN(sec) ? 0 : sec, 0);
                        } else {
                            date = new Date();
                            date.setHours(h, Number.isNaN(m) ? 0 : m, Number.isNaN(sec) ? 0 : sec, 0);
                        }
                        return Math.floor(date.getTime() / 1000);
                    }
                    return 0;
                };
                if (tlogData.time !== undefined && tlogData.time !== '') tlogData.time = toUnixTimestamp(tlogData.time);
                if (tlogData.s_time !== undefined && tlogData.s_time !== '') tlogData.s_time = toUnixTimestamp(tlogData.s_time);
                if (tlogData.e_time !== undefined && tlogData.e_time !== '') tlogData.e_time = toUnixTimestamp(tlogData.e_time);
                // event = time only (seconds since midnight) from start time, no date
                const sTimeNum = typeof tlogData.s_time === 'number' ? tlogData.s_time : 0;
                const startDate = new Date(sTimeNum * 1000);
                tlogData.event = startDate.getHours() * 3600 + startDate.getMinutes() * 60 + startDate.getSeconds();
                // Process all rows with data - do not skip based on defect location so full Excel row count is processed
                processedData.push(tlogData);
            }

            if (errors.length > 0) {
                setValidationErrors(errors);
                Notification.error(`Found ${errors.length} validation error(s). Please check and fix them.`);
            } else {
                Notification.success(`Successfully processed ${processedData.length} row(s)`);
            }
         
            console.log('processedData length', processedData);
            setExcelData(processedData);
        } catch (error) {
            console.error('Error processing spreadsheet file:', error);
            Notification.error('Failed to process file');
        } finally {
            setIsProcessingExcel(false);
        }
    };



    const isAcceptedExcelUpload = (file: File): boolean => {
        const lowerName = (file.name || '').toLowerCase();
        if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || lowerName.endsWith('.csv')) {
            return true;
        }
        const mime = (file.type || '').toLowerCase();
        return (
            mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            mime === 'application/vnd.ms-excel' ||
            mime === 'text/csv' ||
            mime === 'application/csv'
        );
    };

    const handleExcelFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            const file = files[0];
            if (isAcceptedExcelUpload(file)) {
                setExcelFile(file);
                // This dialog now uploads NDT indication logs, so parse rows as repair-log data
                processExcelFile2(file);
            } else {
                Notification.error('Please upload an Excel or CSV file (.xlsx, .xls, or .csv)');
            }
        }
    };

    const handleRemoveExcelFile = () => {
        setExcelFile(null);
        setExcelData([]);
        setValidationErrors([]);
        Notification.success('File removed');
    };

    const handleUpload = async () => {
        if (!excelFile || excelData.length === 0) {
            Notification.error('Please upload and process a file first');
            return;
        }

        if (validationErrors.length > 0) {
            Notification.error('Please fix validation errors before uploading');
            return;
        }

        setIsUploading(true);

        try {
            // Prepare all payloads
            const items = excelData.map((repairLog) => ({
                // If present, include id so backend updates existing row
                ...(repairLog.id ? { id: repairLog.id } : {}),
                project_id: projectId,
                pipe_id: repairLog.pipe_id ?? pipeId,
                subProjectName: repairLog.subProjectName || '',
                weld_id: String(repairLog.weld_id ?? '').trim(),
                date: repairLog.date,
                shift: repairLog.shift || '',
                // Send both camelCase and snake_case to ensure backend updates correctly
                weldersNo: readOptionalString(repairLog.weldersNo ?? repairLog.welders_no),
                welders_no: readOptionalString(repairLog.weldersNo ?? repairLog.welders_no),
                welderRepairNo: readOptionalString(repairLog.welderRepairNo ?? repairLog.welder_repair_no),
                welder_repair_no: readOptionalString(repairLog.welderRepairNo ?? repairLog.welder_repair_no),
                location: repairLog.location || '',
                defectType: repairLog.defectType || repairLog.defectClassification || '',
                defectClassification: repairLog.defectClassification || '',
                defectIdentification: repairLog.defectIdentification || '',
                acceptedWorkmanship: repairLog.acceptedWorkmanship || false,
                defectLocationStart: repairLog.defectLocationStart || 0,
                defectLocationEnd: repairLog.defectLocationEnd || 0,
                defectLength: repairLog.defectLength || 0,
                defectDepth: repairLog.defectDepth || 0,
                defectHeight: repairLog.defectHeight || 0,
                comments: repairLog.comments || '',
                rootCause: repairLog.rootCause || '',
                documents: [],
                documentTags: [],
                pipeDia: repairLog.pipeDia || 20,
                wallThickness: repairLog.wallThickness || 5,
                wpsNumber: repairLog.wpsNumber || '',
                oclockLocation: repairLog.oclockLocation || '',
                repair: repairLog.repair || '',
                rca: repairLog.rca || '',
                criticalWeld: repairLog.criticalWeld || 'No',
                evaluation: repairLog.evaluation || 'Rejected',
                passName: repairLog.passName || repairLog.defectIdentification || '',
                time: repairLog.time || '',
                correctiveActions: repairLog.correctiveActions || '',
                ndtIndicationDocId: repairLog.ndtIndicationDocId ?? []
            }));


            console.log('items', items);
            console.log('items sample (first 3)', items.slice(0, 3).map((r) => ({
                project_id: r.project_id,
                pipe_id: r.pipe_id,
                subProjectName: r.subProjectName,
                weld_id: r.weld_id,
                date: r.date,
                weldersNo: r.weldersNo,
                welders_no: r.welders_no,
                welderRepairNo: r.welderRepairNo,
                welder_repair_no: r.welder_repair_no,
                rca: r.rca,
                rootCause: r.rootCause,
                correctiveActions: r.correctiveActions
            })));
            // Call platform bulk API
                const url = NDT_BULK_PATH;

               const apiPaylod = {
                  items: items
                }
                const response = await axiosConfig.post(url, apiPaylod);
                console.log('response', response);
                if (response.status === 200) {
                    Notification.success(`Successfully uploaded ${excelData.length} repair log(s)`);
                    onUploadComplete?.();

                    // Reset state
                    setExcelFile(null);
                    setExcelData([]);
                    setValidationErrors([]);
                    onClose();
                } else {
                    throw new Error('Failed to upload repair logs');
                }
        } catch (error: any) {
            console.error('Error uploading repair logs:', error);
            Notification.error(
                'Failed to upload repair logs',
                error?.response?.data?.message || error.message || 'Please try again.'
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleUploadTlogData = async () => {
        if (!excelFile || excelData.length === 0) {
            Notification.error('Please upload and process a file first');
            return;
        }

        if (validationErrors.length > 0) {
            Notification.error('Please fix validation errors before uploading');
            return;
        }

        setIsUploading(true);

        try {
            const getpassname = (zone_name: string) => {
                if (!zone_name || typeof zone_name !== 'string') return '';
                const z = zone_name.trim();
                if (z.toLowerCase().includes('root')) return 'Root';
                if (z.toLowerCase().includes('hot')) return 'Hot';
                if (z.toLowerCase().includes('cap')) return 'Cap';
                if (z.includes('Last Fill')) return 'Last Fill';
                if (z.toLowerCase().includes('fill')) return 'Fill';
                return z;
            };
            // Convert weld_time from seconds to minutes (number only, e.g. 1130 s → 18.83)
            const secondsToMinutes = (val: unknown): number => {
                const n = parseFloat(String(val ?? '').replace(/,/g, '').trim());
                return Number.isNaN(n) ? 0 : Math.round((n / 60) * 100) / 100;
            };
            const items = excelData.map((tlogRow) => {
                const passName = getpassname(tlogRow.zone_name);
                return {
                    weld_number: String(tlogRow.weld_number ?? weld.weldNumber ?? weld.weld_number ?? '').trim(),
                    project_id: projectId,
                    pipe_id: pipeId,
                    time: tlogRow.time,
                    s_time: tlogRow.s_time,
                    e_time: tlogRow.e_time,
                    duration: tlogRow.duration,
                    pass_name: passName,
                    pass: passName,
                    zone_name: tlogRow.zone_name,
                    job_number: tlogRow.job_number,
                    travel_speed: tlogRow.travel_speed,
                    current: tlogRow.current,
                    volts: tlogRow.volts,
                    heat: tlogRow.heat,
                    event: tlogRow.event,
                    weld_uid: tlogRow.welduid_s_event_torch,
                    welduid_s_event_torch: tlogRow.welduid_s_event_torch,
                    torch: 'Lead',
                };
            });


           // console.log('items', items);
            // Call platform bulk API (API GW: /data360/bulkCreateUpdateTLogs)
                const url = `bulkCreateUpdateTLogs`;
              //  const first = excelData[0];
             //   const last = excelData[excelData.length - 1];
                const apiPayload = {
                    data: items,
                    trigger_recalculation: true,
                   
                };
              console.log('apiPayload', apiPayload);
              const response = await axiosConfig.post(url, apiPayload);
              console.log('response', response);
                // if (response.status === 200) {
                //     Notification.success(`Successfully uploaded ${excelData.length} repair log(s)`);
                //     onUploadComplete?.();

                //     // Reset state
                //     setExcelFile(null);
                //     setExcelData([]);
                //     setValidationErrors([]);
                //     onClose();
                // } else {
                //     throw new Error('Failed to upload repair logs');
                // }
        } catch (error: any) {
            console.error('Error uploading repair logs:', error);
            Notification.error(
                'Failed to upload repair logs',
                error?.response?.data?.message || error.message || 'Please try again.'
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setExcelFile(null);
        setExcelData([]);
        setIsUploading(false);
        setIsProcessingExcel(false);
        setValidationErrors([]);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-[800px] max-h-[90vh] p-6 flex flex-col overflow-x-hidden">
                <div className="flex items-center gap-3 mb-6 flex-shrink-0">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Upload className="h-5 w-5 text-blue-600" />
                    </div>
                    <DialogTitle className="text-xl font-semibold">
                        Upload NDT Repair Log Data
                    </DialogTitle>
                </div>

                <div className="space-y-6 flex-1 overflow-y-auto overflow-x-hidden">
                    {/* Download Template Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                    1
                                </div>
                                <h3 className="text-lg font-medium">Download Template</h3>
                            </div>
                            <Button
                                onClick={handleDownloadTemplate}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                            >
                                <Download className="h-4 w-4" />
                                Download Template
                            </Button>
                        </div>
                        <p className="text-sm text-gray-600">
                            Download the Excel template file to fill in the NDT repair log data. The template includes all required columns.
                        </p>
                    </div>

                    {/* Upload spreadsheet section */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-gray-300 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                2
                            </div>
                            <h3 className="text-lg font-medium">Upload data file</h3>
                        </div>
                        <p className="text-sm text-gray-600">
                            Upload an Excel or CSV file (.xlsx, .xls, or .csv) with the same columns as the template.
                        </p>

                        {excelFile ? (
                            <div className="flex items-center p-3 bg-green-50 border rounded-lg">
                                <FileText className="h-4 w-4 text-green-600 mr-2" />
                                <span className="text-sm text-green-700 flex-1">{excelFile.name}</span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleRemoveExcelFile}
                                    className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        ) : (
                            <FileUpload
                                onFileSelect={handleExcelFileSelect}
                                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/csv"
                                multiple={false}
                                buttonText="Upload Excel or CSV"
                                variant="outline"
                                size="lg"
                                className="w-full"
                            />
                        )}

                        {/* Excel Processing Status */}
                        {excelFile && isProcessingExcel && (
                            <div className="flex items-center gap-2 p-3 bg-blue-50 border rounded-lg">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-sm text-blue-700">Processing file...</span>
                            </div>
                        )}

                        {/* Validation Errors */}
                        {validationErrors.length > 0 && (
                            <div className="p-3 bg-red-50 border rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                    <span className="text-sm font-medium text-red-800">
                                        Validation Errors ({validationErrors.length})
                                    </span>
                                </div>
                                <div className="max-h-40 overflow-y-auto">
                                    <ul className="list-disc list-inside text-xs text-red-700 space-y-1">
                                        {validationErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Success Message */}
                        {excelData.length > 0 && validationErrors.length === 0 && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-sm text-green-700">
                                    Successfully processed {excelData.length} row(s). Ready to upload.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Upload Summary */}
                    {excelFile && excelData.length > 0 && (
                        <div className="p-4 bg-blue-50 border rounded-lg">
                            <h4 className="text-sm font-medium text-blue-800 mb-2">Upload Summary</h4>
                            <div className="space-y-1 text-sm text-blue-700">
                                <div>• File: {excelFile.name}</div>
                                <div>• Total Rows: {excelData.length}</div>
                                <div>• Validation Errors: {validationErrors.length}</div>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-3 mt-6 flex-shrink-0">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isUploading}
                        className="min-w-[100px]"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!excelFile || excelData.length === 0 || isUploading || validationErrors.length > 0}
                        className="bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white min-w-[100px] disabled:bg-gray-400 disabled:cursor-not-allowed"
                        loading={isUploading}
                        loadingText="Uploading..."
                    >
                        <div className="flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Upload
                        </div>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

